import { useState, useEffect, useMemo, Fragment } from 'react';

import { PackPopupType } from '@nf/types/promotion';
import { RewardScene, VoucherInfo, VoucherType, Labels } from '@/types';
import { voucherService } from '../../services/voucher.service';

import { useReward } from '@/contexts/reward-context';
import { ShopLayout } from '@/layouts/shop-layout';
import { VoucherItem } from '../voucher-item';
import { VoucherUsageTutorial } from '../voucher-usage-tutorial';
import { BeginnersPackProgress } from '@/components/beginners-pack-progress';

const createTabs = (labels?: Labels) => [
	{
		id: 'all',
		label: labels?.lbl_all,
		filter: () => true
	},
	{
		id: 'gift',
		label: labels?.lbl_giftedvoucher,
		filter: voucherService.isGiftVoucher
	},
	{
		id: 'shop',
		label: labels?.lbl_purchasedvoucher,
		filter: (v: VoucherInfo) => [1, 2, 3, 4, 5, 8, 9, 10].includes(v.VoucherType)
	},
] as const;

type TabType = ReturnType<typeof createTabs>[number]['id'];

export const VoucherList = () => {
	const {
		labels,
		hasReward,
		cwcBetAmountData,
		setRewardScene,
		getAvailableVouchers,
		voucherCondition,
		onGetPackPopupStatesPromise,
		onKafkaSet
	} = useReward();
	const [activeTab, setActiveTab] = useState<TabType>(hasReward ? 'all' : 'gift');
	const [vouchers, setVouchers] = useState<Array<VoucherInfo>>([]);
	const [canShowTutorial, setCanShowTutorial] = useState(false);
	const isFreeBetEnabled = voucherCondition.enableFreeBet;
	const visibleVouchers = useMemo(
		() => vouchers.filter(voucher => isFreeBetEnabled || voucher.VoucherType !== VoucherType.FreeBet),
		[vouchers, isFreeBetEnabled]
	);

	const defaultTabs = createTabs(labels);
	let tabs = [...defaultTabs];

	if (!hasReward) {
		tabs = tabs.filter(tab => tab.id === 'gift');
	}

	const counts = tabs.reduce((acc, tab) => ({
		...acc,
		[tab.id]: visibleVouchers.filter(tab.filter).length
	}), {} as Record<TabType, number>);

	const filteredVouchers = visibleVouchers.filter(
		tabs.find(tab => tab.id === activeTab)?.filter ?? (() => true)
	);

	const nicknameVouchers = filteredVouchers.filter(v => v.VoucherType === 5);
	const otherVouchers = filteredVouchers.filter(v => v.VoucherType !== 5);
	const processedVouchers = voucherService.sortByEffective([
		...otherVouchers,
		...(nicknameVouchers.length > 0 ? [nicknameVouchers[0]] : [])
	]);

	useEffect(() => {
		const fetchPackPopupStates = async () => {
			if (onGetPackPopupStatesPromise) {
				try {
					const data = await onGetPackPopupStatesPromise([PackPopupType.Tutorial]);
					setCanShowTutorial(data.Data?.[0].IsShow ?? false);
				} catch (error) {
					console.error('Failed to get pack popup states:', error);
					setCanShowTutorial(false);
				}
			}
		};
	
		fetchPackPopupStates();
	}, [onGetPackPopupStatesPromise]);

	useEffect(() => {
		getAvailableVouchers()
			.then(data => {
				setVouchers(data);
			});
	}, []);

	const switchToVoucherRecords = () => {
		setRewardScene(RewardScene.VoucherRecord);
		onKafkaSet({
			type: 3,
			action: 20
		});
	};

	const switchTab = (tabType: TabType) => () => {
		setActiveTab(tabType);
	};

	return (
		<ShopLayout
			header={labels?.lbl_RPMyVoucher}
			headerRightContent={
				<div
					className="reward-top-bar__right-button"
					onClick={switchToVoucherRecords}
				>
					{labels?.lbl_RPVoucherRecords}
				</div>
			}
			containerClassName="shop-single-bg"
		>
			{visibleVouchers.length === 0 ? (
				<div className="reward-empty">
					<div className="reward-empty__img reward-empty__img--voucher"></div>
					<div className="reward-empty__text">{hasReward ? labels?.lbl_RPNoVoucher : labels?.lbl_obp17}</div>
				</div>
			) : (
				<>
					<div className="shop-outline-tabs">
						{tabs.map(((tab, index) => (
							<Fragment key={tab.id}>
								<div
									className="shop-outline-tab"
									data-selected={tab.id === activeTab}
									onClick={switchTab(tab.id)}
								>
									{tab.label} ({counts[tab.id]})
								</div>

								{index === 0 && tabs.length > 1 ? (
									<div className="shop-outline-tab__divider"></div>
								) : null}
							</Fragment>
						)))}
					</div>

					{
						processedVouchers.length > 0 ?
							(processedVouchers.map((voucher, idx) => (
								<VoucherItem
									key={`${voucher.Id}_${idx}`}
									voucher={voucher}
									count={voucher.VoucherType === VoucherType.NicknameChange
										? nicknameVouchers.length
										: 0
									}
								/>
							))) : (
								<div className="reward-empty">
									<div className="reward-empty__img reward-empty__img--voucher"></div>
									<div className="reward-empty__text">{activeTab !== 'gift' ? labels?.lbl_RPNoVoucher : labels?.lbl_obp17}</div>
								</div>)
					}

					{canShowTutorial ? (
						<VoucherUsageTutorial />
					) : null}
				</>
			)}

			{cwcBetAmountData ? (
				<BeginnersPackProgress betAmountData={cwcBetAmountData} />
			) : null}
		</ShopLayout>
	);
};